import React, { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  useQuery,
  useMutation,
  useLazyQuery,
  useApolloClient
} from '@apollo/client'
import Row from 'antd/lib/row'
import Table from 'antd/lib/table'
import Switch from 'antd/lib/switch'
import Button from 'antd/lib/button'
import Tooltip from 'antd/lib/tooltip'
import message from 'antd/lib/message'
import { Discount } from '@slerp/controls'
import { descriptionText, scheduleText, numberOfUsesText } from './Helpers'
import { EditOutlined, QuestionCircleOutlined } from '@ant-design/icons'
import {
  GET_MERCHANT_STORES,
  TOGGLE_DISCOUNT_ACTIVATION_STATUS
} from './Actions'
import { useSession } from '@slerp/accounts'
import dayjs from 'dayjs'
import useGetMerchantDeliveryPricingBands from 'components/CustomHooks/DeliveryPricingbands/useGetMerchantDeliveryPricingBands'
import { getDiscountProperties } from './utils'
import { QUERY_MERCHANT_DISCOUNTS } from 'components/Merchants/MerchantQueries'
import { DiscountFilter, DiscountType } from './types'
import { TableCellNoWrap } from 'components/Common'

interface DiscountsTableProps {
  trigger: 'code' | 'automatic'
  type: DiscountType
  filter: DiscountFilter
  search: string
}

interface Store {
  id: string
  slug: string
  name: string
}

const { Column } = Table

const DiscountsTable = (props: DiscountsTableProps) => {
  const { filter, type, search } = props
  const navigate = useNavigate()
  const client = useApolloClient()
  const { merchant } = useSession()
  const [discounts, setDiscounts] = useState<Discount[]>([])
  const [page, setPage] = useState<number>(1)
  const [totalEntries, setTotalEntries] = useState<number>(0)

  const [toggleDiscountActivation, { loading }] = useMutation(
    TOGGLE_DISCOUNT_ACTIVATION_STATUS,
    {
      fetchPolicy: 'no-cache'
    }
  )

  const { data: stores } = useQuery(GET_MERCHANT_STORES, {
    variables: { merchantId: merchant?.id },
    fetchPolicy: 'no-cache'
  })

  const [getDiscounts, { loading: discountLoading }] = useLazyQuery(
    QUERY_MERCHANT_DISCOUNTS,
    {
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
      onCompleted: (result) => {
        if (result) {
          const {
            getMerchantDiscounts: { discounts, totalEntries }
          } = result

          setDiscounts(discounts)
          setTotalEntries(totalEntries)
        }
      }
    }
  )

  useEffect(() => {
    getDiscounts({
      variables: { merchantId: merchant?.id, page, filter, type, search }
    })
  }, [page, filter, type, search])

  const [deliveryPricingBands] = useGetMerchantDeliveryPricingBands({
    merchantId: merchant?.id || ''
  })

  const merchantStoresDeliveryPricingBands = useMemo(() => {
    return Object.keys(deliveryPricingBands).filter((storeId: string) => {
      const { sameday = [], preorder = [] } = deliveryPricingBands[storeId]
      return !!(sameday || []).length || !!(preorder || []).length
    })
  }, [deliveryPricingBands])

  const storesWithPricingBands = (stores?.stores || [])
    .filter(({ id }: Store) => merchantStoresDeliveryPricingBands.includes(id))
    .map(({ name }: Store) => name)

  const handleToggleDiscountActivation = (
    discountId: string,
    discountCode: string,
    checked: boolean
  ) => {
    toggleDiscountActivation({
      variables: {
        merchantId: merchant?.id,
        discountId: discountId,
        published_at: checked ? dayjs().format() : null
      }
    })
      .then((result) => {
        message.success(
          <span>
            {checked ? 'Activated' : 'Deactivated'}{' '}
            <strong>{discountCode}</strong> successfully!
          </span>,
          5
        )
        client.resetStore()
      })
      .catch((error) => {
        message.error(
          `Failed to ${
            checked ? 'activate' : 'deactivate'
          } discount due to: ${error}`
        )
      })
  }

  const getDiscountEditRoute = (discount: Discount): string => {
    const discountTarget = discount.target.toUpperCase()

    if (discountTarget === 'DELIVERY_FEE') {
      return `/discounts/delivery-charge/${discount.id}`
    }

    const DELIVERY_OR_BASKET_DISCOUNT_TYPES = [
      'ALL_CHARGES',
      'SUBTOTAL_DELIVERY_FEE'
    ]

    const isBasketRoute =
      DELIVERY_OR_BASKET_DISCOUNT_TYPES.includes(discountTarget)

    return isBasketRoute
      ? `/discounts/basket/${discount.id}`
      : `/discounts/product/${discount.id}`
  }

  return (
    <Table
      data-testid='discounts-list-component'
      dataSource={discounts}
      loading={discountLoading}
      pagination={{
        current: page,
        total: totalEntries,
        pageSize: 10,
        onChange: (p) => setPage(p)
      }}
    >
      <Column
        dataIndex='id'
        key='activate-discount'
        render={(value: string, discount: Discount) => {
          const { isBlockedByActivePricingBands } = getDiscountProperties({
            ...discount,
            merchantStoresDeliveryPricingBands:
              merchantStoresDeliveryPricingBands
          })
          const pricingBandBlockMessage = isBlockedByActivePricingBands && (
            <>
              <span>
                The following locations currently have active tiered delivery
                pricing:
              </span>
              <br />
              <br />
              {storesWithPricingBands.map((storeName: string) => (
                <>
                  <span>{storeName}</span>
                  <br />
                </>
              ))}
              <br />
              <span>
                Please either remove these locations from being selected in this
                discount or remove the tiered delivery pricing from the above
                locations.
              </span>
            </>
          )

          return (
            <Tooltip placement='rightTop' title={pricingBandBlockMessage}>
              <Switch
                loading={loading}
                disabled={loading || isBlockedByActivePricingBands}
                onChange={(checked) =>
                  handleToggleDiscountActivation(
                    discount.id,
                    discount.code,
                    checked
                  )
                }
                checked={dayjs(discount.published_at).isValid()}
              />
            </Tooltip>
          )
        }}
      />
      <Column
        title={
          <Row className='_mb-0' align='middle'>
            Code
            <Tooltip
              title={
                <span>Click on the discount code to see full details.</span>
              }
            >
              <QuestionCircleOutlined className='_ml-8' />
            </Tooltip>
          </Row>
        }
        dataIndex='code'
        key='code'
        render={(value: string, discount: Discount) => {
          return (
            <Link to={getDiscountEditRoute(discount)}>
              <strong className='_cl-primary'>{value}</strong>
            </Link>
          )
        }}
      />
      <Column
        title='Description'
        dataIndex='id'
        key='value'
        render={(_id: string, discount: Discount) => {
          return discount.description || descriptionText(discount)
        }}
      />
      <Column
        title='Dates active'
        dataIndex='id'
        key='dates_active'
        render={(id: string) => {
          const discount =
            discounts.find((disc: Discount) => disc.id === id) || {}
          return Object.keys(discount).length && scheduleText(discount)
        }}
      />
      <Column
        title='Number of uses'
        dataIndex='id'
        key='number_of_uses'
        render={(id: string) => {
          const discount =
            discounts.find((disc: Discount) => disc.id === id) ?? {}
          return Object.keys(discount).length && numberOfUsesText(discount)
        }}
      />
      <Column
        title={<TableCellNoWrap>Actions</TableCellNoWrap>}
        dataIndex='id'
        key='actions'
        render={(value: string, discount: Discount) => {
          return (
            <Tooltip title='Edit' color='#d092dd'>
              <Button
                data-testid={`discount-edit-button-${discount.code}`}
                icon={<EditOutlined />}
                size='small'
                onClick={() => navigate(getDiscountEditRoute(discount))}
              />
            </Tooltip>
          )
        }}
      />
    </Table>
  )
}

export default DiscountsTable
